import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../../../../components/sidebar";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "./style.scss";
import * as Yup from "yup";
import { useFormik } from "formik";
import { categoryList } from "../../../../services/category.service";
import { getInviteUserList } from "../../../../core/data/redux/manageUser/_manageUserAction";
import { useDispatch, useSelector } from "react-redux";
import {
  addProperty,
  propertyDetails,
  updateProperty,
} from "../../../../core/data/redux/property/_propertyAction";
import { all_routes } from "../../../router/all_routes";

// 1. Load Google Places script **once** (outside React tree)
const GOOGLE_MAPS_API_KEY = "AIzaSyC8MVw26l8ezCzslweNGSdRnGWsOuGjaNI";

const loadGooglePlacesScript = (callback: () => void) => {
  if ((window as any).google?.maps?.places) {
    callback();
    return;
  }

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
};

const AddProperties: React.FC = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();

  const { id } = useParams<{ id: string }>(); // id in route => edit mode
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const routes = all_routes;

  const [categories, setCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [isOwnershipDropdownOpen, setOwnershipDropdownOpen] = useState(false);
  const [selectedOwnership, setSelectedOwnership] = useState<string | null>(
    null
  );

  const isEdit = Boolean(id);
  const userList = useSelector((state: any) => state?.manageUser?.userList);

  const ownershipOptions: any = [
    { label: "Sole Owner", value: "sole-owner" },
    { label: "Partnership", value: "partnership" },
    { label: "Corporate", value: "corporate" },
  ];

  // 2. Ref for the autocomplete instance
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 3. Load script + initialise Autocomplete **once**
  useEffect(() => {
    loadGooglePlacesScript(() => {
      if (!inputRef.current) return;

      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["address_components", "formatted_address"],
          types: ["address"], // restrict to real addresses
        }
      );

      autoCompleteRef.current = autocomplete;

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (!place?.address_components) {
          // No valid place → keep fields empty
          formik.setFieldValue("address", "");
          formik.setFieldValue("city", "");
          formik.setFieldValue("state", "");
          formik.setFieldValue("zipcode", "");
          return;
        }

        const address = place.formatted_address ?? "";
        const components = place.address_components!;

        // Helper to fetch a component by type(s)
        const getComponent = (types: string[]) =>
          components.find((c) => c.types.some((t) => types.includes(t)))
            ?.long_name ?? "";

        const city =
          getComponent(["locality"]) ||
          getComponent(["sublocality"]) ||
          getComponent(["administrative_area_level_3"]) ||
          "";

        const state = getComponent(["administrative_area_level_1"]);
        const zipcode = getComponent(["postal_code"]);

        formik.setFieldValue("address", address);
        formik.setFieldValue("city", city);
        formik.setFieldValue("state", state);
        formik.setFieldValue("zipcode", zipcode);
      });
    });
  }, []); // run once

  useEffect(() => {
    getCategoryList();
    dispatch(getInviteUserList({}));
  }, []);

  const getCategoryList = async () => {
    try {
      const response = await categoryList();
      const data = response?.data?.data || [];

      // Convert API response into {label, value}
      const mapped = data.map((cat: any) => ({
        label: cat?.name.toUpperCase(),
        value: cat?.uuid,
      }));
      setCategories(mapped);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  //  Check if spouse exists
  const hasSpouse = userList?.some(
    (user: any) =>
      user?.status?.toLowerCase() === "accept" &&
      user?.role?.toLowerCase() === "spouse" &&
      user?.archieve === false
  );

  const initialValues = {
    name: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    ownership: "",
    category: "",
    spouse: false,
  };
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Property name is required")
      .trim()
      .test(
        "no-whitespace-only",
        "Name cannot be empty or just whitespace",
        (value) => value !== undefined && value.trim().length > 0
      )
      .min(2, "Name must be at least 2 characters"),

    address: Yup.string()
      .trim()
      .required("Address is required")
      .test("not-empty", "Address cannot be empty or just spaces", (value) => {
        return value != null && value.trim().length > 0;
      }),

    city: Yup.string()
      .trim()
      .required("City is required")
      .test("not-empty", "City cannot be empty or just spaces", (value) => {
        return value != null && value.trim().length > 0;
      }),

    state: Yup.string()
      .trim()
      .required("State is required")
      .test("not-empty", "State cannot be empty or just spaces", (value) => {
        return value != null && value.trim().length > 0;
      }),

    zipcode: Yup.string()
      .required("ZIP Code is required")
      .trim()
      .test(
        "is-valid-zipcode",
        "ZIP code must be exactly 5 or 6 digits",
        (value) => {
          if (!value) return false;
          const digits = value.replace(/\D/g, ""); // remove any non-digits (in case user types space or dash)
          return digits.length === 5 || digits.length === 6;
        }
      ),
    // .matches(/^\d{5,6}$/, {
    //   message: "ZIP code must be 5 or 6 digits only",
    //   excludeEmptyString: true,
    // })
    // .test("valid-zip-format", "Invalid ZIP code format", (value) => {
    //   if (!value) return false;
    //   const digitsOnly = value.replace(/\D/g, "");
    //   const len = digitsOnly.length;
    //   return len === 5 || len === 6;
    // }),
    ownership: Yup.string().required("Ownership Type is required"),
    category: Yup.string().required("Category is required"),
    spouse: Yup.boolean().oneOf([true, false], "Please select spouse option"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,

    onSubmit: async (values) => {
      try {
        if (isEdit) {
          await dispatch(updateProperty({ uuid: id, values }));
        } else {
          await dispatch(addProperty(values));
        }
        navigate(routes.properties);
      } catch (error) {
        console.error("Error saving property", error);
      }
    },
  });

  // Load property details if editing
  useEffect(() => {
    if (isEdit) {
      const fetchData = async () => {
        try {
          const response = await dispatch(propertyDetails({ uuid: id }));
          const propertyData = response?.payload?.data;

          const data = {
            name: propertyData?.name || "",
            address: propertyData?.address || "",
            city: propertyData?.city || "",
            state: propertyData?.state || "",
            zipcode: propertyData?.zipcode || "",
            category: propertyData?.category?.uuid || "",
            spouse: propertyData?.spouse ? true : false,
            ownership: propertyData?.ownership || "",
          };
          formik.setValues(data);

          // Preselect ownership label
          const matched = ownershipOptions.find(
            (opt: any) => opt.value === propertyData?.ownership
          );
          if (matched) {
            setSelectedOwnership(matched.label);
          }
        } catch (error) {
          console.error("Failed to load property", error);
        }
      };
      fetchData();
    }
  }, [id]);

  return (
    <div className="layout">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} />

      {/* Main Content */}
      <main className="content">
        <div className="profileSettings add-properties">
          <div className="add-properties-heading d-flex align-items-center gap-3">
            <img
              className="img-fluid"
              src="/assets/img/building-image.svg"
              alt="building"
            />
            <h2 className="mb-0">
              {isEdit ? "Edit Property" : "Add New Property"}
            </h2>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="row mb-3 mt-4">
              <div className="col-md-6 mb-3 mb-md-0">
                <label htmlFor="name" className="form-label">
                  Property Name
                </label>
                <input
                  type="text"
                  className="form-control commonInput bg-light"
                  id="name"
                  placeholder="Enter Property Name"
                  {...formik.getFieldProps("name")}
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue("name", trimmedValue);
                    formik.handleBlur(e); // keep touched state
                  }}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-danger">{formik.errors.name}</div>
                )}
              </div>

              {/* ---------- GOOGLE AUTOCOMPLETE ADDRESS ---------- */}
              <div className="col-md-6">
                <label htmlFor="text" className="form-label">
                  Complete Address
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  className="form-control commonInput bg-light"
                  id="address"
                  placeholder="Enter Complete Address"
                  // {...formik.getFieldProps("address")}
                  value={formik.values.address}
                  onChange={(e) =>
                    formik.setFieldValue("address", e.target.value)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.address && formik.errors.address && (
                  <div className="text-danger">{formik.errors.address}</div>
                )}
                <span className="auto">
                  Address will auto-populate city, state, and ZIP code
                </span>
              </div>
            </div>

            {/* ----- CITY / STATE / ZIP (still editable) ----- */}
            <div className="row mb-3 mt-4">
              <div className="col-md-4 mb-3 mb-md-0">
                <label htmlFor="text" className="form-label">
                  City
                </label>
                <input
                  type="text"
                  className="form-control commonInput bg-light"
                  id="city"
                  placeholder="Enter City"
                  {...formik.getFieldProps("city")}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className="text-danger">{formik.errors.city}</div>
                )}
              </div>

              <div className="col-md-4 mb-3 mb-md-0">
                <label htmlFor="state" className="form-label">
                  State
                </label>
                <input
                  type="text"
                  className="form-control commonInput bg-light"
                  id="state"
                  placeholder="Enter State"
                  {...formik.getFieldProps("state")}
                />
                {formik.touched.state && formik.errors.state && (
                  <div className="text-danger">{formik.errors.state}</div>
                )}
              </div>

              <div className="col-md-4">
                <label htmlFor="zipcode" className="form-label">
                  ZIP Code
                </label>
                <input
                  type="number"
                  // type="text"   /* keep as text – Google sometimes returns leading zeros */
                  className="form-control commonInput bg-light"
                  id="zipcode"
                  placeholder="Enter ZIP Code"
                  {...formik.getFieldProps("zipcode")}
                />
                {formik.touched.zipcode && formik.errors.zipcode && (
                  <div className="text-danger">{formik.errors.zipcode}</div>
                )}
              </div>
            </div>

            {/* Ownership Type */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                <label className="form-label">Ownership Type</label>
                <div className="dropdown" id="reporting-year-dropdown">
                  <button
                    className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput"
                    onClick={(e) => {
                      e.preventDefault();
                      setOwnershipDropdownOpen(!isOwnershipDropdownOpen);
                    }}
                    type="button"
                  >
                    {selectedOwnership || "Select Ownership Type "}
                    <i
                      className={`bi bi-chevron-${
                        isOwnershipDropdownOpen ? "up" : "down"
                      }`}
                    />
                  </button>

                  {isOwnershipDropdownOpen && (
                    <div
                      className="dropdown-menu show w-100 shadow-sm mt-1 bg-light p-0"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {ownershipOptions.map((type: any) => (
                        <div
                          key={type?.value}
                          className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom"
                          onClick={() => {
                            formik.setFieldValue("ownership", type?.value);
                            setSelectedOwnership(type?.label);
                            setOwnershipDropdownOpen(false);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <span>{type?.label}</span>
                          <input
                            type="radio"
                            name="ownershipDropdown"
                            className=""
                            checked={formik.values?.ownership === type?.value}
                            readOnly
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formik.touched.ownership && formik.errors.ownership && (
                  <div className="text-danger mt-1">
                    {formik.errors.ownership}
                  </div>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="user-type-box mb-4 p-3 rounded bg-light form-control commonInput">
              <label className="form-label">Choose Category</label>

              <div className="btn-radio d-flex flex-wrap">
                {categories.map((cat) => (
                  <div key={cat?.value} className="me-3">
                    <input
                      type="radio"
                      id={`category-${cat?.value || "both"}`}
                      name="category"
                      value={cat.value}
                      checked={formik?.values?.category === cat?.value}
                      onChange={formik.handleChange}
                    />
                    <label
                      htmlFor={`category-${cat?.value || "both"}`}
                      className="radio-label"
                    >
                      {cat?.label}
                    </label>
                  </div>
                ))}
              </div>

              {formik.touched.category && formik.errors.category && (
                <div className="text-danger">{formik.errors.category}</div>
              )}
            </div>

            {/* Spouse */}
            {hasSpouse && (
              <div className="user-type-box mb-4 p-3 rounded bg-light form-control commonInput">
                <label className="form-label">
                  Do you want to Assign Spouse ?
                </label>
                <div className="btn-radio d-flex">
                  <div className="me-3">
                    <input
                      type="radio"
                      id="spouse-yes"
                      name="spouse"
                      value="true"
                      checked={formik.values?.spouse === true}
                      onChange={() => formik.setFieldValue("spouse", true)}
                    />
                    <label htmlFor="spouse-yes" className="radio-label">
                      YES
                    </label>
                  </div>

                  <div className="me-3">
                    <input
                      type="radio"
                      id="spouse-no"
                      name="spouse"
                      value="false"
                      checked={formik.values?.spouse === false}
                      onChange={() => formik.setFieldValue("spouse", false)}
                    />
                    <label htmlFor="spouse-no" className="radio-label">
                      NO
                    </label>
                  </div>
                </div>

                {formik.touched.spouse && formik.errors.spouse && (
                  <div className="text-danger">{formik.errors.spouse}</div>
                )}
              </div>
            )}

            <div className="text-end">
              <button
                type="submit"
                className="btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center  btn-block"
              >
                {isEdit ? "Update Property" : "Save Property"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AddProperties;
